import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Colours from '@shared/ui/atoms/Colours';
import { BodyRegular, BodySmall } from '@shared/ui/atoms/fonts/Body';
import { HeadlineSmall } from '@shared/ui/atoms/fonts/Headline';
import { selectDailyFilter, setDailyFilter } from '@features/whereabouts/store/whereaboutsView.duck';
import { dailyFilterDescriptor } from './dailyFilterLabels';
import {
  EmptyTeamMessage,
  RestrictedMessage,
  ShowAllButton,
  TeamHeader,
  TeamSection,
} from './WhereaboutsV2Styles';

interface GroupSectionProps {
  title: string;
  restricted?: boolean;
  children: React.ReactNode;
}

// One followed team/workgroup section: heading, restricted notice, or member rows.
export function WhereaboutsGroupSection({ title, restricted, children }: GroupSectionProps) {
  const { t } = useTranslation();
  return (
    <TeamSection>
      <TeamHeader><HeadlineSmall>{title}</HeadlineSmall></TeamHeader>
      {restricted ? (
        <RestrictedMessage><BodySmall colour={Colours.darkGrey}>{t('whereabouts-v2.restricted')}</BodySmall></RestrictedMessage>
      ) : children}
    </TeamSection>
  );
}

interface EmptyMessageProps {
  period: 'today' | 'week';
}

// Shown when the active daily filter hides every member of a group.
export function FilteredGroupEmptyMessage({ period }: EmptyMessageProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const filter = useSelector(selectDailyFilter);
  const descriptor = dailyFilterDescriptor(filter, t);
  const message = period === 'today'
    ? t('whereabouts-v2.no-team-members-matching-today', 'No team member {{descriptor}} today', { descriptor })
    : t('whereabouts-v2.no-team-members-matching', 'No team member {{descriptor}} this week', { descriptor });

  return (
    <EmptyTeamMessage>
      <BodyRegular colour={Colours.darkGrey}>{message}</BodyRegular>
      <ShowAllButton onClick={() => dispatch(setDailyFilter(null))}>
        {t('whereabouts-v2.show-all-team-members', 'Show all')}
      </ShowAllButton>
    </EmptyTeamMessage>
  );
}
